import type { CookieConsentConfig, CookieValue } from "vanilla-cookieconsent";

export type OnConsentCb = (param: { cookie: CookieValue }) => void;
export type OnChangeCb = (param: { cookie: CookieValue }) => void;

export function getCookieConsentConfig(
    activeLang: string,
    onConsentCb?: OnConsentCb,
    onChangeCb?: OnChangeCb
): CookieConsentConfig {
    /* eslint-disable max-len */
    return {
        autoClearCookies: true,
        guiOptions: {
            consentModal: {
                position: "bottom left"
            }
        },
        categories: {
            necessary: {
                enabled: true,
                readOnly: true
            },
            analytics: {
                enabled: true,
                readOnly: false,
                autoClear: {
                    cookies: [
                        {
                            name: "_ga"
                        },
                        {
                            name: "_ga_FF4CZKWFBC"
                        }
                    ]
                }
            }
        },
        language: {
            default: activeLang,
            translations: {
                fr: {
                    consentModal: {
                        title: "Consentement d'utilisation de cookies",
                        description:
                            "Bien que certains cookies soient nécessaires au bon fonctionnement de notre site web, nous n'utilisons pas de cookies tiers, publicitaires ou de suivi.",
                        acceptAllBtn: "Accepter tous",
                        acceptNecessaryBtn: "Refuser tous",
                        showPreferencesBtn: "Gérer les préférences",
                        footer: "<a href='/fr/confidentialite'>Politique de confidentialité</a>"
                    },
                    preferencesModal: {
                        title: "Gérer les préférences de cookies",
                        acceptAllBtn: "Accepter tous",
                        acceptNecessaryBtn: "Refuser tous",
                        savePreferencesBtn: "Accepter la sélection actuelle",
                        closeIconLabel: "Fermer les préférences",
                        sections: [
                            {
                                title: "Préférences de cookies",
                                description:
                                    "Vous pouvez gérer les préférences de cookies en utilisant le bouton ci-dessous."
                            },
                            {
                                title: "Cookies strictement nécessaires",
                                description:
                                    "Ces cookies sont essentiels au bon fonctionnement de notre site web et ne peuvent être désactivés.",
                                linkedCategory: "necessary"
                            },
                            {
                                title: "Performance et Analytiques",
                                description:
                                    "Ces cookies collectent des informations sur la façon dont vous utilisez notre site web. Toutes les données sont anonymisées et ne peuvent pas être utilisées pour vous identifier.",
                                linkedCategory: "analytics",
                                cookieTable: {
                                    headers: {
                                        name: "Nom du cookie",
                                        domain: "Domaine",
                                        description: "Description"
                                    },
                                    body: [
                                        {
                                            name: "_ga",
                                            domain: "packbo.net",
                                            description:
                                                "Cookie utilisé pour le suivi des performances et des interactions utilisateur de façon anonyme."
                                        },
                                        {
                                            name: "_ga_FF4CZKWFBC",
                                            domain: "packbo.net",
                                            description:
                                                "Cookie utilisé pour le suivi des performances et des interactions utilisateur de façon anonyme."
                                        }
                                    ]
                                }
                            },
                            {
                                title: "Nous ne vous suivons pas sur ce site",
                                description:
                                    "Nous ne vous suivons pas sur ce site, et ne partageons pas vos données avec des tiers. Nous n'utilisons pas de cookies de suivi. Pour plus d'informations, veuillez consulter notre politique de confidentialité, et nos termes et conditions d'utilisation."
                            }
                        ]
                    }
                },
                en: {
                    consentModal: {
                        title: "Cookie usage consent",
                        description:
                            "Although some cookies are necessary for the proper functioning of our website, we do not use third-party, advertising or tracking cookies.",
                        acceptAllBtn: "Accept all",
                        acceptNecessaryBtn: "Reject all",
                        showPreferencesBtn: "Manage preferences",
                        footer: "<a href='/en/privacy'>Privacy Policy</a>"
                    },
                    preferencesModal: {
                        title: "Manage cookie preferences",
                        acceptAllBtn: "Accept all",
                        acceptNecessaryBtn: "Reject all",
                        savePreferencesBtn: "Accept current selection",
                        closeIconLabel: "Close preferences",
                        sections: [
                            {
                                title: "Cookie preferences",
                                description: "You can manage cookie preferences using the button below."
                            },
                            {
                                title: "Strictly necessary cookies",
                                description:
                                    "These cookies are essential for the proper functioning of our website and cannot be disabled.",
                                linkedCategory: "necessary"
                            },
                            {
                                title: "Performance and Analytics",
                                description:
                                    "These cookies collect information about how you use our website. All data is anonymized and cannot be used to identify you.",
                                linkedCategory: "analytics",
                                cookieTable: {
                                    headers: {
                                        name: "Cookie name",
                                        domain: "Domain",
                                        description: "Description"
                                    },
                                    body: [
                                        {
                                            name: "_ga",
                                            domain: "packbo.net",
                                            description:
                                                "Cookie used to track user performance and interactions anonymously."
                                        },
                                        {
                                            name: "_ga_FF4CZKWFBC",
                                            domain: "packbo.net",
                                            description:
                                                "Cookie used to track user performance and interactions anonymously."
                                        }
                                    ]
                                }
                            },
                            {
                                title: "We do not track you on this site",
                                description:
                                    "We do not track you on this site, and do not share your data with third parties. We do not use tracking cookies. For more information, please refer to our privacy policy, and our terms and conditions of use."
                            }
                        ]
                    }
                }
            }
        },
        onConsent: onConsentCb
            ? (param: { cookie: CookieValue }) => {
                  onConsentCb(param);
              }
            : undefined,
        onChange: onChangeCb
            ? (param: { cookie: CookieValue }) => {
                  onChangeCb(param);
              }
            : undefined
    };
    /* eslint-enable max-len */
}
